import type {
  MergeConflictV1,
  MergeResponseV1,
  PresenceV1,
} from "./types";
import { clearElement, createElement, prettyJson } from "../ui/dom";

const STATUS_LABELS: Readonly<Record<MergeResponseV1["status"], string>> = {
  merged: "Merged",
  conflict: "Conflict",
  unsupported: "Unsupported",
  invalid_input: "Invalid input",
  resource_limit: "Resource limit",
};

function renderPresence(label: string, presence: PresenceV1): HTMLElement {
  const item = createElement("div", "presence");
  item.append(createElement("span", "presence__label", label));

  if (presence.state === "missing") {
    item.append(createElement("span", "presence__missing", "missing"));
    return item;
  }

  const value = createElement("code", "presence__value");
  value.textContent = JSON.stringify(presence.value);
  item.append(value);
  return item;
}

function renderConflict(conflict: MergeConflictV1, index: number): HTMLElement {
  const card = createElement("article", "conflict-card");
  const heading = createElement("div", "conflict-card__heading");
  heading.append(
    createElement("span", "conflict-card__number", `Conflict ${index + 1}`),
    createElement("code", "conflict-card__path", conflict.path || "<root>"),
  );
  card.append(heading);
  card.append(createElement("p", "conflict-card__kind", conflict.kind));

  const states = createElement("div", "presence-grid");
  states.append(
    renderPresence("Base", conflict.base),
    renderPresence("Source", conflict.source),
    renderPresence("Target", conflict.target),
  );
  card.append(states);
  return card;
}

function renderErrorFields(error: object): HTMLElement {
  const list = createElement("dl", "error-fields");
  for (const [key, value] of Object.entries(error)) {
    const term = createElement("dt", undefined, key.replaceAll("_", " "));
    const description = createElement("dd");
    const code = createElement("code");
    code.textContent =
      typeof value === "string"
        ? value || "<root>"
        : typeof value === "number"
          ? value.toLocaleString("en-US")
          : JSON.stringify(value);
    description.append(code);
    list.append(term, description);
  }
  return list;
}

export function renderMergeResponse(
  container: HTMLElement,
  response: MergeResponseV1,
): void {
  clearElement(container);
  container.dataset.status = response.status;

  const summary = createElement("div", "result-summary");
  summary.append(
    createElement(
      "span",
      `status-badge status-badge--${response.status}`,
      STATUS_LABELS[response.status],
    ),
  );
  container.append(summary);

  switch (response.status) {
    case "merged": {
      container.append(
        createElement(
          "p",
          "result-lede",
          "The operation produced one complete merged JSON value.",
        ),
      );
      const output = createElement("pre", "json-output");
      output.textContent = prettyJson(response.merged);
      container.append(output);
      break;
    }
    case "conflict": {
      container.append(
        createElement(
          "p",
          "result-lede",
          `${response.conflicts.length.toLocaleString()} ordered conflict${response.conflicts.length === 1 ? "" : "s"}; no partial merged value was returned.`,
        ),
      );
      const conflictList = createElement("div", "conflict-list");
      response.conflicts.forEach((conflict, index) => {
        conflictList.append(renderConflict(conflict, index));
      });
      container.append(conflictList);
      break;
    }
    case "unsupported": {
      container.append(
        createElement(
          "p",
          "result-lede",
          "An array was found outside an array-aware profile. The operation failed closed.",
        ),
        renderErrorFields(response.error),
      );
      break;
    }
    case "invalid_input": {
      container.append(
        createElement(
          "p",
          "result-lede",
          "Strict input admission rejected one document before merge processing.",
        ),
        renderErrorFields(response.error),
      );
      break;
    }
    case "resource_limit": {
      container.append(
        createElement(
          "p",
          "result-lede",
          "The disclosed engine resource policy stopped processing without a partial result.",
        ),
        renderErrorFields(response.error),
      );
      break;
    }
  }
}
